import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  MapPin, 
  Train, 
  Calendar, 
  Camera, 
  Upload, 
  Navigation,
  ExternalLink,
  X,
  User,
  Edit,
  Route as RouteIcon,
  Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import DepotMap from "@/components/DepotMap";
import { PhotoCard } from "@/components/PhotoCard";
import { AmtrakArrivals } from "@/components/AmtrakArrivals";
import { RailCamCard } from "@/components/RailCamCard";
import { AdminEditDepotDialog } from "@/components/AdminEditDepotDialog";
import { useTripCart } from "@/lib/tripCart";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [depot, setDepot] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    image: null,
    caption: "",
    uploadedBy: "",
    contributorEmail: "",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const { has: tripHas, add: tripAdd, remove: tripRemove } = useTripCart();

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setCurrentUser(r.data))
      .catch(() => setCurrentUser(null));
  }, []);

  const openUploadModal = () => {
    // Upload is public — no auth required. Modal collects name + email inline.
    setUploadOpen(true);
  };

  const fetchDepot = useCallback(async () => {
    try {
      setLoading(true);
      const [depotRes, photosRes] = await Promise.all([
        axios.get(`${API}/depots/${id}`),
        axios.get(`${API}/depots/${id}/photos`)
      ]);
      setDepot(depotRes.data);
      setPhotos(photosRes.data);
    } catch (error) {
      console.error("Error fetching depot:", error);
      toast.error("Failed to load depot details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDepot();
  }, [fetchDepot]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadData(prev => ({ ...prev, image: event.target.result }));
        setPreviewImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.image) {
      toast.error("Please select an image");
      return;
    }
    // For anonymous uploaders, require name + email. Signed-in users skip this.
    if (!currentUser) {
      if (!uploadData.uploadedBy.trim() || !uploadData.contributorEmail.trim() || !uploadData.contributorEmail.includes("@")) {
        toast.error("Please tell us your name and email so we can credit you.");
        return;
      }
    }

    try {
      setUploading(true);
      await axios.post(
        `${API}/depots/${id}/photos`,
        {
          image_data: uploadData.image,
          caption: uploadData.caption || null,
          uploaded_by: uploadData.uploadedBy || null,
          contributor_email: uploadData.contributorEmail || null,
        },
        { withCredentials: true }
      );

      toast.success("Photo uploaded successfully!");
      setUploadOpen(false);
      setUploadData({ image: null, caption: "", uploadedBy: "", contributorEmail: "" });
      setPreviewImage(null);
      fetchDepot();
    } catch (error) {
      console.error("Upload error:", error);
      if (error.response?.status === 413) {
        toast.error("Image too large — please use a photo under 4.5 MB");
      } else {
        toast.error(error.response?.data?.detail || "Failed to upload photo");
      }
    } finally {
      setUploading(false);
    }
  };

  const openGoogleMaps = () => {
    if (depot) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${depot.latitude},${depot.longitude}`;
      window.open(url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-white/50">Loading depot...</p>
        </div>
      </div>
    );
  }

  if (!depot) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl font-bold mb-2 text-white">Depot Not Found</h2>
          <p className="text-white/50 mb-4">The depot you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/")} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Map
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif font-bold text-lg truncate text-white">{depot.name}</h1>
              <p className="text-sm text-white/50">{depot.city}, {depot.state}</p>
            </div>
            <Button 
              onClick={openGoogleMaps}
              className="hidden sm:flex bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="directions-btn"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Directions
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Map */}
            <Card className="bg-[#111] border-white/5" data-testid="depot-map-card">
              <CardContent className="p-0 overflow-hidden rounded-xl">
                <div className="h-[300px] md:h-[400px]">
                  <DepotMap 
                    depots={[depot]} 
                    onDepotClick={() => {}}
                    center={[depot.latitude, depot.longitude]}
                    zoom={14}
                    darkMode={true}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Photos Section */}
            <Card className="bg-[#111] border-white/5" data-testid="photos-card">
              <CardHeader className="flex flex-row items-center justify-between border-b border-white/5">
                <CardTitle className="font-serif text-white flex items-center gap-2">
                  <Camera className="w-5 h-5 text-amber-500" />
                  Photos ({photos.length})
                </CardTitle>
                <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                  <Button
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                    data-testid="upload-photo-btn"
                    onClick={openUploadModal}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                  <DialogContent className="sm:max-w-md bg-[#1a1a1a] border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white">Upload Photo</DialogTitle>
                      <DialogDescription className="text-white/50">
                        Share a photo of {depot.name}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {previewImage ? (
                        <div className="relative">
                          <img 
                            src={previewImage} 
                            alt="Preview" 
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              setPreviewImage(null);
                              setUploadData(prev => ({ ...prev, image: null }));
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                          onClick={() => document.getElementById("photo-input")?.click()}
                        >
                          <Camera className="w-10 h-10 mx-auto mb-3 text-white/20" />
                          <p className="text-sm text-white/50">
                            Click to select an image
                          </p>
                          <p className="text-xs text-white/30 mt-1">
                            Max 5MB, JPG or PNG
                          </p>
                        </div>
                      )}
                      <input
                        id="photo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageSelect}
                        data-testid="photo-file-input"
                      />
                      <div>
                        <label className="text-sm font-medium mb-2 block text-white/80">Caption (optional)</label>
                        <Textarea
                          placeholder="Describe this photo..."
                          value={uploadData.caption}
                          onChange={(e) => setUploadData(prev => ({ ...prev, caption: e.target.value }))}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          data-testid="photo-caption-input"
                        />
                      </div>
                      {currentUser ? (
                        <p className="text-xs text-white/60">
                          Uploading as <span className="text-white/90 font-medium">{currentUser.name || currentUser.email}</span>
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <label className="text-sm font-medium mb-2 block text-white/80">Your name *</label>
                            <Input
                              placeholder="Jane Doe"
                              value={uploadData.uploadedBy}
                              onChange={(e) => setUploadData(prev => ({ ...prev, uploadedBy: e.target.value }))}
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                              data-testid="photo-uploader-input"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block text-white/80">Your email *</label>
                            <Input
                              type="email"
                              placeholder="never shown publicly"
                              value={uploadData.contributorEmail}
                              onChange={(e) => setUploadData(prev => ({ ...prev, contributorEmail: e.target.value }))}
                              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                              data-testid="photo-contributor-email-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-white/10 text-white hover:bg-white/5">
                        Cancel
                      </Button>
                      <Button onClick={handleUpload} disabled={!uploadData.image || uploading} className="bg-amber-500 hover:bg-amber-600 text-black">
                        {uploading ? "Uploading..." : "Upload Photo"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-5">
                {photos.length > 0 ? (
                  <div className="photo-gallery">
                    {photos.map((photo) => (
                      <PhotoCard
                        key={photo.id}
                        photo={photo}
                        subjectId={depot.id}
                        subjectKind="depot"
                        onClick={() => setLightboxImage(photo)}
                        canDelete={!!(currentUser?.is_admin || currentUser?.is_mod)}
                        onDelete={(id) => setPhotos((prev) => prev.filter((p) => p.id !== id))}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Camera className="w-16 h-16 mx-auto mb-4 text-white/10" />
                    <p className="text-white/40 mb-4">No photos yet</p>
                    <Button variant="outline" onClick={openUploadModal} className="border-white/10 text-white hover:bg-white/5">
                      <Upload className="w-4 h-4 mr-2" />
                      Be the first to add a photo
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info Card */}
            <Card className="bg-[#111] border-white/5" data-testid="depot-info-card">
              <CardHeader className="border-b border-white/5">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-serif text-xl text-white">{depot.name}</CardTitle>
                  <Badge className={`${depot.status === "original" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30"}`}>
                    {depot.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">{depot.city}, {depot.state}</p>
                    {depot.address && (
                      <p className="text-sm text-white/50">{depot.address}</p>
                    )}
                  </div>
                </div>

                {depot.railroad && (
                  <div className="flex items-start gap-3">
                    <Train className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-white">{depot.railroad}</p>
                      <p className="text-sm text-white/50">Railroad</p>
                    </div>
                  </div>
                )}

                {depot.year_built && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-white">{depot.year_built}</p>
                      <p className="text-sm text-white/50">Year Built</p>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-white/5">
                  <p className="text-xs text-white/30 font-mono">
                    {depot.latitude.toFixed(6)}, {depot.longitude.toFixed(6)}
                  </p>
                  {depot.updated_at && (
                    <p
                      className="text-[11px] text-white/50 font-mono mt-1.5"
                      data-testid="depot-updated-chip"
                      title={`Last edited by ${depot.updated_by || "unknown"}`}
                    >
                      Updated {new Date(depot.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card className="bg-[#111] border-white/5">
              <CardContent className="p-4 space-y-3">
                <Button 
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black" 
                  onClick={openGoogleMaps}
                  data-testid="get-directions-btn"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Get Directions
                </Button>
                <Button
                  variant="outline"
                  className="w-full bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30"
                  asChild
                  data-testid="street-view-btn"
                >
                  <a
                    href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${depot.latitude},${depot.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Google Street View
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30"
                  onClick={openUploadModal}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Add Photo
                </Button>
                <Button
                  variant="outline"
                  className={`w-full ${
                    tripHas(depot.id)
                      ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/25 hover:!text-emerald-200"
                      : "bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30"
                  }`}
                  onClick={() =>
                    tripHas(depot.id) ? tripRemove(depot.id) : tripAdd(depot)
                  }
                  data-testid="add-to-trip-btn"
                >
                  {tripHas(depot.id) ? (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Added to Trip
                    </>
                  ) : (
                    <>
                      <RouteIcon className="w-4 h-4 mr-2" /> Add to Trip
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full bg-white/[0.06] border-white/20 text-white hover:bg-white/[0.14] hover:!text-white hover:border-white/30"
                  asChild
                  data-testid="suggest-correction-btn"
                >
                  <Link to={`/depot/${depot.id}/edit`}>
                    <Edit className="w-4 h-4 mr-2" />
                    Suggest a Correction
                  </Link>
                </Button>

                {(currentUser?.is_admin || currentUser?.is_mod) && (
                  <AdminEditDepotDialog
                    depot={depot}
                    onChange={(updated) => setDepot((d) => ({ ...d, ...updated }))}
                  />
                )}
                <Button 
                  variant="ghost" 
                  className="w-full text-white/70 hover:text-amber-500 hover:bg-white/5"
                  asChild
                >
                  <Link to={`/states/${depot.state_code}`}>
                    View All {depot.state} Depots
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Link
                  to="/submit"
                  className="block text-center text-xs text-white/60 hover:text-amber-500 pt-1 pb-0.5 transition-colors"
                  data-testid="submit-new-depot-link"
                >
                  Missing a depot? Submit it →
                </Link>
              </CardContent>
            </Card>

            {/* Museum / visiting info */}
            {(depot.museum_status || depot.visiting_hours || depot.on_active_rail_line) && (
              <Card className="bg-[#111] border-white/5" data-testid="museum-info-card">
                <CardContent className="p-4 space-y-3">
                  {depot.museum_status && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1.5">Status</p>
                      <span className={
                        "inline-block px-3 py-1 rounded-full text-xs font-semibold " +
                        {
                          active_station: "bg-emerald-500/20 text-emerald-400",
                          museum: "bg-amber-500/20 text-amber-400",
                          restored_display: "bg-sky-500/20 text-sky-400",
                          adaptive_reuse: "bg-purple-500/20 text-purple-400",
                          abandoned: "bg-red-500/20 text-red-400",
                          demolished: "bg-white/10 text-white/50",
                        }[depot.museum_status] || "bg-white/10 text-white/70"
                      }>
                        {depot.museum_status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </div>
                  )}
                  {depot.visiting_hours && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Visiting hours</p>
                      <p className="text-sm text-white/85 whitespace-pre-line">{depot.visiting_hours}</p>
                    </div>
                  )}
                  {depot.on_active_rail_line && (
                    <p className="text-xs text-emerald-400 flex items-center gap-1.5">
                      <Train className="w-3.5 h-3.5" />
                      Trains still pass this location
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Live RailCam (visible to public if active; admins/mods see edit tools) */}
            <RailCamCard
              depot={depot}
              canEdit={!!(currentUser?.is_admin || currentUser?.is_mod)}
              onChange={(updated) => setDepot((d) => ({ ...d, ...updated }))}
            />

            {/* Amtrak live arrivals — only if this depot has a station code */}
            {depot.amtrak_station_code && (
              <AmtrakArrivals depotId={depot.id} stationCode={depot.amtrak_station_code} />
            )}

            {/* Preserved Equipment on-site */}
            {depot.preserved_equipment && (
              <Card className="bg-[#111] border-white/5" data-testid="preserved-equipment-card">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3 text-white flex items-center gap-2">
                    <Train className="w-4 h-4 text-amber-500" />
                    Preserved Equipment on Site
                  </h4>
                  <div className="text-sm text-white/80 whitespace-pre-line font-mono leading-relaxed">
                    {depot.preserved_equipment}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status Info */}
            <Card className="bg-[#111] border-white/5">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2 text-white">Depot Status</h4>
                <p className="text-sm text-white/50">
                  {depot.status === "original" 
                    ? "This depot is located at or near its original site."
                    : "This depot has been relocated from its original location."
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.image_data} 
              alt={lightboxImage.caption || "Depot photo"} 
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            {(lightboxImage.caption || lightboxImage.uploaded_by) && (
              <div className="mt-4 text-center text-white">
                {lightboxImage.caption && (
                  <p className="text-lg">{lightboxImage.caption}</p>
                )}
                <p className="text-sm text-white/50 flex items-center justify-center gap-1 mt-1">
                  <User className="w-3 h-3" />
                  {lightboxImage.uploaded_by}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
